import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, NgModule, Provider } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import {
    CancellationComponentInstanceToken,
    DeviceContextInterceptor,
    ENDPOINTS,
    HttpOnlineInterceptor,
    MockModeInterceptorService,
    NotificationService,
    OdataConfigService,
    OdataValueHelperService,
    PACE_PLATFORM,
    PaceCoreModule,
    SynchronizationService,
    TranslationService,
} from '@evorait/pace-core';
import {
    ActionUtilitiesInstanceToken,
    ActionUtilitiesService,
    ConfirmationInstanceToken,
    FormsWriteWithPaceOfflineService,
    PaceForms2Module,
} from '@evorait/pace-forms';
import {
    FileStorageOfflineStrategyService,
    IndexeddbOfflineStrategyService,
    OfflineInterceptorService,
    PaceOfflineModule,
} from '@evorait/pace-offline';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
// These imports are supposed to solve problems with angular zones where a next/complete makes the subscribe callback
// run in the zone of the next/complete instead of the zone of the subscribe. This causes problems with cordova plugins
// because now all next/complete in the plugin callback run outside of the angular zone.
// The patches fix that, but unfortunately bring along their own bugs. These bugs would case a "RangeError: Stack size
// exceeded" error eg. while tapping on the whatsnew icon, which results in a crashed application.
// To mitigate the problem, we manually use promises to trigger the data delivery from the cordova callbacks and then
// convert the promises to observables in a second step.
// import 'zone.js/dist/zone-patch-cordova';
// import 'zone.js/dist/zone-patch-rxjs';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { ProgressbarConfig, ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { RatingModule } from 'ngx-bootstrap/rating';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ClipboardModule } from 'ngx-clipboard';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { APP_VERSION } from '../environments/appversion';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { routes } from './app.routes';
import { ConfirmationModule } from './confirmation/confirmation.module';
import { ConfirmationService } from './confirmation/services/confirmation.service';
import { CoreModule } from './core/core.module';
import { metaReducers, reducers } from './core/reducers';
import { ConfigInterfaceService } from './core/services/config-interface/config-interface.service';
import { InitializationService } from './core/services/initialization/initialization.service';
import { DashboardModule } from './dashboard/dashboard.module';
import { DetailsModule } from './details/details.module';
import { TravelStatusUpdateService } from './details/travel-status-update/travel-status-update.service';
import { FormsListWrapperComponent } from './pace-forms-2-wrapper/components/forms2-list/forms-list-wrapper.component';
import { PaceForms2WrapperModule } from './pace-forms-2-wrapper/pace-forms-2-wrapper.module';
import { PaceSideMenuService } from './pace-side-menu.service';
import { CancellationComponent } from './shared/components/cancellation/cancellation.component';
import { SharedModule } from './shared/shared.module';
import { TimesheetModule } from './timesheet/timesheet.module';
import { ServiceWorkerModule } from '@angular/service-worker';
import { combineLatest, Observable, take } from 'rxjs';
import { ServiceWorkerReadyService } from './core/services/service-worker-ready/service-worker-ready.service';
import {
    InitialiserDependency,
    PaceAuth0AuthService,
    PaceMsalAuthenticationModule,
    PaceMsalAuthService,
} from '@evorait/pace-authentication';
import { DocBoxTabbedModalModule } from './docbox-tabbed-modal/docbox-tabbed-modal.module';
import { CommonModule } from '@angular/common';
import { PaceAuth0AuthenticationModule } from '../../../../libs/pace-authentication/src/lib/pace-auth0-authentication.module';

// configure defaults for @ngx-bootstrap progress bar components
export function getProgressBarConfig(): ProgressbarConfig {
    return Object.assign(new ProgressbarConfig(), {
        animate: true,
        type: 'info',
        striped: true,
    });
}

const appInitialiserDependencies: Array<typeof InitialiserDependency> = [ServiceWorkerReadyService];
if (environment.useMsal) appInitialiserDependencies.push(PaceMsalAuthService);
else if (environment.auth0?.clientId) appInitialiserDependencies.push(PaceAuth0AuthService);

export function appInitializerFactory(
    serviceWorkerReadyService: ServiceWorkerReadyService,
    authService?: PaceAuth0AuthService | PaceMsalAuthService,
): () => Observable<unknown> {
    const dependencies: Array<Observable<unknown>> = [serviceWorkerReadyService.ready$()];

    if (environment.useMsal && authService) {
        dependencies.push(authService.ready$());
    } else if (environment.auth0?.clientId && authService) {
        dependencies.push(authService.ready$());
    }

    return (): Observable<unknown> => combineLatest(dependencies).pipe(take(1));
}

let interceptorProviders: Provider[] = [
    // http interceptor for intercepting authentication requests
    { provide: HTTP_INTERCEPTORS, useClass: OfflineInterceptorService, multi: true },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: HttpOnlineInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: DeviceContextInterceptor,
        multi: true,
    },
];

if (environment.operateInMockMode) {
    interceptorProviders.push({
        provide: HTTP_INTERCEPTORS,
        useClass: MockModeInterceptorService,
        multi: true,
    });
}
// PACE Online does not need offline service, so we only need http online interceptor to forward our request
// Cannot use ClientManagerService yet since services are not available yet at this stage, therefore appid workaround
// TODO: Decide remove if PACE PWA and PACE Online are decided to be the same software
if (environment.appId.includes('paceonline')) {
    interceptorProviders = [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpOnlineInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: DeviceContextInterceptor,
            multi: true,
        },
    ];
}

const authModule = [];
if (environment.useMsal) {
    authModule.push(PaceMsalAuthenticationModule.forRoot(environment));
} else if (environment.auth0?.clientId) {
    authModule.push(PaceAuth0AuthenticationModule.forRoot(environment));
}

@NgModule({
    bootstrap: [AppComponent],
    declarations: [AppComponent],
    imports: [
        CommonModule,
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        ProgressbarModule.forRoot(),
        ModalModule.forRoot(),
        BsDropdownModule.forRoot(),
        PopoverModule.forRoot(),
        AccordionModule.forRoot(),
        RatingModule.forRoot(),
        TabsModule.forRoot(),
        CarouselModule.forRoot(),
        LoggerModule.forRoot({ level: NgxLoggerLevel.DEBUG }),
        TranslateModule.forRoot(),
        StoreModule.forRoot(reducers, {
            metaReducers,
            runtimeChecks: {
                strictStateImmutability: true,
                strictActionImmutability: true,
                strictStateSerializability: true,
                strictActionSerializability: true,
                strictActionWithinNgZone: true,
                strictActionTypeUniqueness: true,
            },
        }),
        // Instrumentation must be imported after importing StoreModule (config is optional)
        // this allows to debug the store using a Chrome extension as described in the angular documentation:
        // https://ngrx.io/guide/store-devtools
        StoreDevtoolsModule.instrument({
            maxAge: 25, // Retains last 25 states
            logOnly: environment.production, // Restrict extension to log-only mode
        }),
        EffectsModule.forRoot([]),
        RouterModule.forRoot(routes, { enableTracing: false, useHash: true }),
        CoreModule,
        SharedModule,
        PaceForms2Module.forRoot({
            notificationService: NotificationService,
            formsWriteService: FormsWriteWithPaceOfflineService,
            formListComponent: FormsListWrapperComponent,
        }),
        ConfirmationModule,
        TimesheetModule,
        DetailsModule,
        DashboardModule,
        ClipboardModule,
        ZXingScannerModule,
        DocBoxTabbedModalModule,
        TooltipModule.forRoot(),
        PaceCoreModule.forRoot({
            initializationService: InitializationService,
            odataConfigService: OdataConfigService,
            odataValueHelper: OdataValueHelperService,
            sideMenuService: PaceSideMenuService,
            synchronizationService: SynchronizationService,
            configInterfaceService: ConfigInterfaceService,
            environment: environment,
            appVersion: APP_VERSION,
        }),
        PaceOfflineModule.forRoot(
            [
                ENDPOINTS.ConfigSet,
                ENDPOINTS.ValueHelpers,
                ENDPOINTS.NotificationHeaders,
                ENDPOINTS.F4mReleaseLock,
                ENDPOINTS.F4mSubmitFormSet,
                ENDPOINTS.F4mSubmitParticipantCopySet,
                ENDPOINTS.MaterialStockSet,
                ENDPOINTS.InputFieldProperties,
                ENDPOINTS.CseDopSet,
                ENDPOINTS.WhatsNew,
                ENDPOINTS.WorkScheduleSet,
                ENDPOINTS.DeepInsertSet,
                ENDPOINTS.NotificationCancellation,
                ENDPOINTS.RequestNotification,
                ENDPOINTS.MaterialSerialNumbersGet,
                ENDPOINTS.MeasToolSet,
                ENDPOINTS.DocboxUploadSet,
            ],
            environment.platform === PACE_PLATFORM.IOS
                ? FileStorageOfflineStrategyService
                : IndexeddbOfflineStrategyService,
        ),
        PaceForms2WrapperModule,
        ServiceWorkerModule.register('pace-pwa-worker.js', {
            enabled: environment.production,
            registrationStrategy: 'registerImmediately',
        }),
        ...authModule,
    ],
    providers: [
        provideHttpClient(withInterceptorsFromDi()),
        // to be provided in the app, as we do not want to provide it in the core module
        ...interceptorProviders,

        // configuration of @ngx-bootstrap modules (see below for factory)
        { provide: ProgressbarConfig, useFactory: getProgressBarConfig },
        { provide: CancellationComponentInstanceToken, useValue: CancellationComponent },
        { provide: ConfirmationInstanceToken, useExisting: ConfirmationService },
        {
            provide: ActionUtilitiesInstanceToken,
            useExisting: ActionUtilitiesService,
        },
        {
            provide: APP_INITIALIZER, // TODO Angular ^19: deprecated with angular v19. change to provideAngularInitializer().
            useFactory: appInitializerFactory,
            deps: appInitialiserDependencies,
            multi: true,
        },
        TravelStatusUpdateService,
    ],
})
export class AppModule {
    public constructor(
        private readonly translationService: TranslationService,
        private readonly translate: TranslateService,
    ) {
        // load default language translation (for fallback)
        const defaultTranslation = <Record<string, unknown>>(
            // eslint-disable-next-line @typescript-eslint/no-require-imports,@typescript-eslint/no-var-requires
            require(`../assets/i18n/${this.translationService.defaultLanguageKey}.json`)
        );
        this.translate.setTranslation(translationService.defaultLanguageKey, defaultTranslation, true);

        const lang = this.translationService.getLanguageKey();
        if (lang !== this.translationService.defaultLanguageKey) {
            // load device's language translation
            // eslint-disable-next-line @typescript-eslint/no-require-imports,@typescript-eslint/no-var-requires
            const translation = <Record<string, unknown>>require(`../assets/i18n/${lang}.json`);
            this.translate.setTranslation(lang, translation, true);
        }

        this.translationService.setLanguageKey(lang);
    }
}
